import { getEnvironment } from "../environment/Environment";
import { v4 as uuid } from 'uuid';
import startCase from 'lodash/startCase';
import camelCase from 'lodash/camelCase';

export interface ModalHandle {
   page: string;
   location?: string;
   title?: string;
}

type Map<T> = { [key: string]: T };

interface TimingArgs {
   event_category: string;
   name: string;
   value: number;
   event_label?: string;
}

let timingQueue: Map<TimingArgs> = {};
let isInitialized = false;

let curPageTitle: string | undefined;
let curPagePath: string | undefined;
let curPageLocation: string = window.location.href;

/**
 * Call this only from tests, it resets the internal state
 * of this module
 */
export const reset = () => {
   isInitialized = false;
};

const initializeGoogleAnalytics = (): boolean => {

   if (!(window as any).unifocus || !(window as any).unifocus.trackAnalyticsEvent) {
      console.warn("initializeGoogleAnalytics: trackAnalyticsEvent function does not exist for the unifocus environment");
      return false;
   }

   timingQueue = {};

   isInitialized = true;

   return true;
};

/**
 * Start time tracking
 *
 * @param {string} category
 * @param {string} name
 * @param {string} label
 * @returns {string} The key to pass to trackTimingEnd, which actually sends the timing information
 */
export const trackTimingStart = (category: string, name: string, label?: string): string => {
   if (!isInitialized) {
      if (!initializeGoogleAnalytics()) {
         return '';
      }
   }

   const trackingKey = uuid();

   timingQueue[trackingKey] = {
      event_category: category,
      name: name,
      value: new Date().getTime(),
      event_label: label
   };

   return trackingKey;
};

/**
 * End time tracking
 *
 * @param {string} trackingKey The key obtained from trackTimingStart
 * @returns {boolean} false if something detectable went wrong
 */
export const trackTimingEnd = (trackingKey: string): boolean => {
   if (!isInitialized) {
      if (!initializeGoogleAnalytics()) {
         return false;
      }
   }

   const timingArgs = timingQueue[trackingKey];

   if (timingArgs == null) {
      console.warn("trackTimingEnd: time tracking not sent:  tracking key \"" + trackingKey + "\" not found!");
      return false;
   }

   delete timingQueue[trackingKey];

   timingArgs.value = new Date().getTime() - timingArgs.value;

   (window as any).unifocus.trackAnalyticsEvent('timing_complete', timingArgs);

   return true;
};

/**
 * Track an event
 *
 * @param {string} category
 * @param {string} action
 * @param {string} label
 * @param {number} value
 * @param {boolean} nonInteraction
 * @returns {boolean} false if something detectable went wrong
 */
export const trackEvent = (category: string,
                           action: string,
                           label?: string,
                           value?: number,
                           nonInteraction?: boolean): boolean => {
   if (!isInitialized) {
      if (!initializeGoogleAnalytics()) {
         return false;
      }
   }

   (window as any).unifocus.trackAnalyticsEvent(action, {
      event_category: category,
      event_action: action,
      event_label: label,
      value: value,
      non_interaction: nonInteraction
   });

   return true;
};

/**
 * Track a page view
 *
 * @param {string} path
 * @param {string | undefined} title
 * @returns {boolean} false if something detectable went wrong
 */
export const trackPageView = (path: string, title?: string | undefined): boolean => {
   if (!isInitialized) {
      if (!initializeGoogleAnalytics()) {
         return false;
      }
   }

   if (!title) {
      const applicationId = getEnvironment().applicationId;

      if (applicationId) {
         title = startCase(camelCase(applicationId.toString()));
      }
   }

   curPageTitle = title;
   curPagePath = path;
   curPageLocation = window.location.href;

   (window as any).unifocus.trackAnalyticsEvent('page_view', {
      page_title: title,
      page_path: path
              });

   return true;
};

/**
 * Track a virtual page view for a modal open
 *
 * @param {string} path
 * @returns {ModalHandle} A ModalHandle to pass back to trackModalClose
 */
export const trackModalOpen = (path: string): ModalHandle => {
   const modalHandle: ModalHandle = {
      page: ''
   };

   if (!isInitialized) {
      if (!initializeGoogleAnalytics()) {
         return modalHandle;
      }
   }

   modalHandle.page = curPagePath || '';
   modalHandle.title = curPageTitle;
   modalHandle.location = curPageLocation;

   (window as any).unifocus.trackAnalyticsEvent('page_view', {
      page_path: path
   });

   return modalHandle;
};

/**
 * Restore the previous page view after a modal close
 *
 * @param modalHandle {ModalHandle} The ModalHandle obtained from a call to trackModalOpen
 *
 * @returns {boolean} false if something detectable went wrong
 */
export const trackModalClose = (modalHandle: ModalHandle): boolean => {
   if (!isInitialized) {
      if (!initializeGoogleAnalytics()) {
         return false;
      }
   }

   (window as any).unifocus.trackAnalyticsEvent('page_view', {
      page_title: modalHandle.title,
      page_path: modalHandle.page,
      page_location: modalHandle.location
              });

   return true;
};

/**
 * Track an exception
 * @param {string} description
 * @param {boolean} isFatal
 * @returns {boolean} false if something detectable went wrong
 */
export const trackException = (description: string, isFatal: boolean): boolean => {
   if (!isInitialized) {
      if (!initializeGoogleAnalytics()) {
         return false;
      }
   }

   (window as any).unifocus.trackAnalyticsEvent('exception', {
                       description: description,
                       fatal: isFatal
                    });

   return true;
};
